import { useEffect, useRef, useCallback } from 'react';
import { supabase } from '../utils/supabase';

const DEFAULT_DEBOUNCE_MS = 320;

/**
 * Subscribes to Supabase Realtime postgres_changes for session rows.
 *
 * Three sync modes are supported:
 *
 *  1. Granular merge (preferred for single-intent row edits):
 *     Provide `onRowChange(table, eventType, newRow, oldRow)`.
 *     Each DB row change is forwarded directly — the caller merges only the
 *     affected court/player into local state without a full reload.
 *     Used for session_courts and session_players.
 *
 *  2. Authoritative full reload:
 *     Provide `onRemoteSync(opts)` where opts may include `{ ignoreSaveGuard: true }`.
 *     Used for session_games (structural command result), session_partnerships,
 *     and as a fallback/reconnect path.
 *
 *  3. Scheduled games refresh (lightweight, no session snapshot side-effects):
 *     Provide `onScheduledGamesChange()`.
 *     Called for every scheduled_games INSERT/UPDATE/DELETE so all host tabs
 *     refresh their local scheduled-games list without triggering a full session
 *     snapshot reload. This keeps scheduled_games as an independent concern —
 *     its lifecycle (create → listed → promoted → consumed) is managed entirely
 *     by action-time paths, not by sync inference.
 *     Requires scheduled_games REPLICA IDENTITY FULL so DELETE events include
 *     session_id and pass the Realtime session_id filter (see migration
 *     20260704000113_scheduled_games_replica_identity_full.sql).
 *     Falls back to scheduleSync() when not provided (backward-compatible).
 *
 * Design principle:
 *   Server transactions that change multiple related rows (e.g. finish_session_match,
 *   select_peg_board_game, finish_peg_board_match)
 *   should trigger a full normalized snapshot reload, not only granular row merges.
 *   Granular merge is correct for single-row edits (drag-drop, manual roster changes).
 *   Structural commands (finish match, Peg Board pick/score, promotion) are signalled via
 *   session_games INSERT and session_players/session_courts updates. Peg Board picker/host
 *   races are rejected in select_peg_board_game via p_expected_picker_id.
 *
 * @param {Object}   params
 * @param {string|null}   params.sessionId
 * @param {boolean}       params.enabled
 * @param {boolean}       params.useNormalizedState  – mirrors sessions.use_normalized_state
 * @param {boolean}       params.isSessionHost
 * @param {() => boolean} [params.isSavingGetter]    – used only for non-authoritative syncs
 * @param {(table: string, eventType: string, newRow: object, oldRow: object) => void} [params.onRowChange]
 *   Called for every row-level change on session_courts and session_players.
 *   When provided, these tables skip the full-reload path.
 * @param {(opts?: { ignoreSaveGuard?: boolean }) => void|Promise<void>} [params.onRemoteSync]
 *   Full-reload callback. Receives { ignoreSaveGuard: true } for Realtime-driven reloads
 *   (e.g. session_games) so host tabs can never block convergence via save-guard.
 *   Upcoming matchmaking after a recorded game runs only in CompetitiveSessionPage
 *   afterFinishMatchSync (generateMissingUpcomingCourts), not inside this hook.
 *   Receives no args / { ignoreSaveGuard: false } only for manual/legacy call sites.
 * @param {() => void|Promise<void>} [params.onScheduledGamesChange]
 *   Lightweight callback invoked on every scheduled_games row change.
 *   Should only refresh the local scheduled-games list (e.g. call loadScheduledGames).
 *   Must NOT trigger a full session snapshot reload or run consumption logic.
 *   Optional — falls back to scheduleSync() when not provided.
 * @param {() => boolean} [params.shouldSkipWriterRealtimeSync]
 *   When true on the writer host, skip redundant session_games authoritative sync
 *   (score path already triggered handleRemoteSessionSync).
 * @param {(result: object) => void} [params.onScoreCommandResult]
 *   Called when a SCORE_COMPLETED action lands in session_host_actions.
 *   Passive tabs use this to apply the authoritative layout payload without a
 *   full session snapshot reload.  The initiator tab should ignore this event
 *   if it already applied the result from the EF response (idempotency via
 *   localLayoutRevisionRef inside applyScoreCompletedResult).
 * @param {(status: string) => void} [params.onSessionStatusChange]
 *   Called when sessions.session_status changes for this session (cross-device lifecycle sync).
 * @param {number}  [params.debounceMs]
 */
export default function useCompetitiveSessionRealtime({
  sessionId,
  enabled,
  useNormalizedState,
  isSessionHost,
  isSavingGetter,
  onRowChange,
  onRemoteSync,
  onScheduledGamesChange,
  onSessionStatusChange,
  shouldSkipWriterRealtimeSync,
  onScoreCommandResult,
  debounceMs = DEFAULT_DEBOUNCE_MS,
}) {
  const debounceTimerRef            = useRef(null);
  const onRemoteSyncRef             = useRef(onRemoteSync);
  const onRowChangeRef              = useRef(onRowChange);
  const onScheduledGamesChangeRef   = useRef(onScheduledGamesChange);
  const onSessionStatusChangeRef  = useRef(onSessionStatusChange);
  const shouldSkipWriterRealtimeSyncRef = useRef(shouldSkipWriterRealtimeSync);
  const onScoreCommandResultRef     = useRef(onScoreCommandResult);
  /** OR-merge debounced opts so session_games (authoritative) is not lost if another table schedules a sync in the same window. */
  const pendingSyncOptsRef = useRef({ ignoreSaveGuard: false });
  onRemoteSyncRef.current           = onRemoteSync;
  onRowChangeRef.current            = onRowChange;
  onScheduledGamesChangeRef.current = onScheduledGamesChange;
  onSessionStatusChangeRef.current  = onSessionStatusChange;
  shouldSkipWriterRealtimeSyncRef.current = shouldSkipWriterRealtimeSync;
  onScoreCommandResultRef.current   = onScoreCommandResult;

  const clearDebounce = useCallback(() => {
    if (debounceTimerRef.current != null) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
  }, []);

  /**
   * Schedule a full-reload sync after a debounce.
   *
   * opts.ignoreSaveGuard = true  → authoritative path (server command completed).
   *   Fires after debounce with no save-state retry; always passes ignoreSaveGuard to callback.
   *   Used for session_games and other structural events.
   *
   * opts.ignoreSaveGuard = false (default) → optimistic path (drag-drop / minor edits).
   *   Retries until host save queue is idle before calling the callback.
   *   Avoids applying a stale snapshot while this tab has unsaved local edits in flight.
   */
  const scheduleSync = useCallback((opts = {}) => {
    const { ignoreSaveGuard = false } = opts;
    pendingSyncOptsRef.current = {
      ignoreSaveGuard:
        pendingSyncOptsRef.current.ignoreSaveGuard || !!ignoreSaveGuard,
    };
    clearDebounce();

    const run = () => {
      debounceTimerRef.current = null;
      const merged = pendingSyncOptsRef.current;
      pendingSyncOptsRef.current = { ignoreSaveGuard: false };
      // Non-authoritative path: wait for host debounced-save queue to drain first.
      if (!merged.ignoreSaveGuard && isSessionHost && isSavingGetter?.()) {
        pendingSyncOptsRef.current = merged;
        debounceTimerRef.current = setTimeout(run, Math.min(debounceMs, 200));
        return;
      }
      const fn = onRemoteSyncRef.current;
      if (fn) {
        Promise.resolve(
          fn({
            ignoreSaveGuard: merged.ignoreSaveGuard,
          })
        ).catch(() => {});
      }
    };

    debounceTimerRef.current = setTimeout(run, debounceMs);
  }, [isSessionHost, isSavingGetter, debounceMs, clearDebounce]);

  useEffect(() => {
    if (!enabled || !sessionId) {
      return undefined;
    }

    const channelName = `competitive-session:${sessionId}`;
    const filter      = `session_id=eq.${sessionId}`;
    const channel     = supabase.channel(channelName);

    // Tables that support granular row-change merging when onRowChange is provided.
    const granularTables = new Set(['session_courts', 'session_players']);

    // Tables whose changes signal a structural multi-row command completed — always
    // trigger an authoritative full reload so passive host tabs converge.
    const authoritativeTables = new Set(['session_games']);

    const subs = [];
    if (useNormalizedState) {
      subs.push(
        { table: 'session_courts',              filter },
        { table: 'session_players',             filter },
        { table: 'session_player_interactions', filter },
        { table: 'session_category_ranges',     filter }
      );
    } else {
      subs.push({ table: 'session_state', filter });
    }
    subs.push(
      { table: 'session_games',        filter },
      { table: 'session_partnerships', filter },
      { table: 'scheduled_games',      filter },
      { table: 'session_host_actions', filter },
      { table: 'sessions',             filter: `id=eq.${sessionId}` },
    );

    subs.forEach(({ table, filter: f }) => {
      channel.on(
        'postgres_changes',
        { event: '*', schema: 'public', table, filter: f },
        (payload) => {
          const hasGranularHandler = typeof onRowChangeRef.current === 'function';

          // Granular path: forward row data for incremental merge (single-row edits).
          if (hasGranularHandler && granularTables.has(table)) {
            onRowChangeRef.current(
              table,
              payload.eventType,
              payload.new,
              payload.old
            );
            return;
          }

          // Session lifecycle: push status changes to all open tabs.
          if (table === 'sessions') {
            const newStatus = payload.new?.session_status;
            if (newStatus && typeof onSessionStatusChangeRef.current === 'function') {
              onSessionStatusChangeRef.current(newStatus);
            }
            return;
          }

          // session_host_actions INSERT — structural command result.
          // SCORE_COMPLETED: deliver the layout payload to passive tabs so they
          // can converge without a full session snapshot reload.
          if (table === 'session_host_actions') {
            if (payload.eventType === 'INSERT') {
              const row = payload.new;
              if (
                row?.action_type === 'SCORE_COMPLETED' &&
                row?.result != null &&
                typeof onScoreCommandResultRef.current === 'function'
              ) {
                try {
                  onScoreCommandResultRef.current(row.result);
                } catch (e) {
                  console.error('[useCompetitiveSessionRealtime] onScoreCommandResult failed:', e);
                }
              }
            }
            return;
          }

          // Authoritative tables (session_games etc.) signal a structural command
          // completed. Always bypass save guard so all host tabs reload.
          if (authoritativeTables.has(table)) {
            if (
              table === 'session_games' &&
              isSessionHost &&
              shouldSkipWriterRealtimeSyncRef.current?.()
            ) {
              return;
            }
            scheduleSync({ ignoreSaveGuard: true });
            return;
          }

          // scheduled_games has its own lightweight handler — just refresh the list,
          // no full session snapshot reload or consumption side-effects.
          if (table === 'scheduled_games' && typeof onScheduledGamesChangeRef.current === 'function') {
            Promise.resolve(onScheduledGamesChangeRef.current()).catch(() => {});
            return;
          }

          // All other tables (partnerships, fallback for non-normalized):
          // debounced reload respecting save state.
          scheduleSync();
        }
      );
    });

    channel.subscribe();

    return () => {
      clearDebounce();
      supabase.removeChannel(channel);
    };
  }, [enabled, sessionId, useNormalizedState, scheduleSync, clearDebounce]);

  // Refetch when tab becomes visible (missed-events / reconnect fallback).
  // Always authoritative — load current DB state regardless of local save status.
  useEffect(() => {
    if (!enabled || !sessionId) {
      return undefined;
    }
    const onVisible = () => {
      if (document.hidden) return;
      clearDebounce();
      const fn = onRemoteSyncRef.current;
      if (fn) Promise.resolve(fn({ ignoreSaveGuard: true })).catch(() => {});
      if (typeof onSessionStatusChangeRef.current === 'function') {
        supabase
          .from('sessions')
          .select('session_status')
          .eq('id', sessionId)
          .maybeSingle()
          .then(({ data }) => {
            if (data?.session_status) {
              onSessionStatusChangeRef.current(data.session_status);
            }
          })
          .catch(() => {});
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [enabled, sessionId, clearDebounce]);
}
